// frontend/src/hooks/useScopeTree.ts — "Zones and clusters" as ONE small picker
// (ADR-050 §3 · ADR-080 §3): the zones as GROUP rows, each zone's clusters under
// it (the family's two-level group row — a zone's box ticks everything inside,
// its clusters then read "Included"; the picker keeps only the highest pick, the
// server keeps it again). Two whole reads composed on the press under the tree's
// own key, a minute fresh; a disabled zone or cluster is an inactive row. The
// values are KINDED (ADR-037 §2 — the territory grammar, `common/territory.ts`):
// `zone:<id>` · `cluster:<id>` — one field holds both kinds, as the Location
// filter does. The lists answer under a named rule (`zones/requests.ts`):
// `manage` (the default — the scope editor: a people manager is offered their
// own-scope rows, a giver grants only what they hold whole; an admin or a
// vocabulary manager every row) · `reach` (a FILTER — any member, the zones and
// clusters holding something their scope reaches: the naming rule, ADR-026 §A —
// the sites list's "Zone & cluster" filter, whose picks travel as the list's
// `territory` param unchanged).

import type { ContainerListRule } from '@ems-app/schemas/zones/requests';
import type { ZonesListResponse } from '@ems-app/schemas/zones/responses';
import type { ClustersListResponse } from '@ems-app/schemas/clusters/responses';
import { parseTerritory, territoryToken } from '@ems-app/schemas/common/territory';
import { apiClient } from '../lib/apiClient.js';
import type { OptionRow, SmallSource } from './useOptions.js';

export interface ScopeTree {
  zones: ZonesListResponse;
  clusters: ClustersListResponse;
}

export const scopeTreeKeys = {
  /** The tree under a rule — `manage` the scope editor's, `reach` a filter's (its own entry under the same prefix). */
  all: (projectId: string, rule: ContainerListRule = 'manage') => (rule === 'reach' ? (['scope-tree', projectId, 'reach'] as const) : (['scope-tree', projectId] as const)),
};

async function fetchScopeTree(projectId: string, rule: ContainerListRule, signal?: AbortSignal): Promise<ScopeTree> {
  const [zones, clusters] = await Promise.all([
    apiClient.get<ZonesListResponse>(`/projects/${projectId}/zones`, { params: { rule }, signal }),
    apiClient.get<ClustersListResponse>(`/projects/${projectId}/clusters`, { params: { rule }, signal }),
  ]);
  return { zones: zones.data, clusters: clusters.data };
}

/** A zone's value in the tree picker. */
export const zoneValue = (id: number): string => territoryToken('zone', id);
/** A cluster's value in the tree picker. */
export const clusterValue = (id: number): string => territoryToken('cluster', id);

/** The picked values back to ids, by kind (the territory grammar's one parser). */
export function splitTreeValues(values: readonly string[]): { zoneIds: number[]; clusterIds: number[] } {
  return parseTerritory(values);
}

/** The tree rows: a zone a group row, its clusters its children — a cluster whose zone the viewer does not see (a
 *  giver who holds clusters alone) stands on its own, a row of the top level after the zones. */
export function scopeTreeRows(tree: ScopeTree): OptionRow[] {
  const zones = new Set(tree.zones.items.map((z) => z.id));
  const byZone = new Map<number, OptionRow[]>();
  const lone: OptionRow[] = [];
  for (const c of tree.clusters.items) {
    const row: OptionRow = { id: clusterValue(c.id), label: c.name, pickable: true, inactive: c.is_disabled, group: null, parent: zoneValue(c.zone_id) };
    if (!zones.has(c.zone_id)) {
      lone.push({ ...row, parent: null });
      continue;
    }
    const rows = byZone.get(c.zone_id) ?? [];
    rows.push(row);
    byZone.set(c.zone_id, rows);
  }
  const grouped = tree.zones.items.map((z): OptionRow => {
    const children = byZone.get(z.id) ?? [];
    return { id: zoneValue(z.id), label: z.name, pickable: true, inactive: z.is_disabled, group: null, children: children.length > 0 ? children : undefined };
  });
  return [...grouped, ...lone];
}

/** Zones and clusters as a picker's source (the small kind) — the group rows above, under a rule: `manage` (the
 *  default) for the scope editor, `reach` for a filter. */
export function scopeTreeSource(projectId: string, rule: ContainerListRule = 'manage'): SmallSource<ScopeTree> {
  return {
    kind: 'small',
    queryKey: scopeTreeKeys.all(projectId, rule),
    fetch: (signal) => fetchScopeTree(projectId, rule, signal),
    rows: scopeTreeRows,
  };
}
